import React, { useCallback, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  ListGroup,
  ListGroupItem,
  Modal,
  Row,
  UncontrolledCarousel,
} from 'reactstrap';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { UnitViewGallery } from '../../pages/pcv/models/unitPcv';

export type Props = {
  gallery: UnitViewGallery[];
  description: string;
  onDelete?: (item: UnitViewGallery) => void;
  onDeleteAll?: () => void;
  onDrag?: (items: UnitViewGallery[]) => void;
};

const viewBadgeStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: 0,
  left: 0,
  right: 0,
  background: 'rgba(0,0,0,0.55)',
  color: '#fff',
  fontSize: 12,
  textAlign: 'center',
  padding: '2px 4px',
  borderRadius: '0 0 4px 4px',
};

const AdminViewGallery: React.FC<Props> = ({
  gallery,
  description,
  onDelete,
  onDeleteAll,
  onDrag,
}) => {
  const validGallery = gallery.filter(
    (item) =>
      item.image &&
      !item.image.toString().includes('null') &&
      !item.image.toString().includes('undefined'),
  );

  const [galleryForDisplay, setGalleryForDisplay] =
    useState<UnitViewGallery[]>(validGallery);
  const [showModal, setShowModal] = useState(false);
  const [currentImage, setCurrentImage] = useState('');

  const previewModal = (image: string) => {
    setCurrentImage(image);
    setShowModal((state) => !state);
  };

  const onDragEnd = useCallback(
    (event: any) => {
      const srcIndex = event.source.index;
      const dstIndex = event.destination.index;

      const reordered = [...galleryForDisplay];
      reordered.splice(dstIndex, 0, reordered.splice(srcIndex, 1)[0]);

      setGalleryForDisplay(reordered);
      if (onDrag) onDrag(reordered);
    },
    [onDrag, galleryForDisplay],
  );

  const imageThumbnail = (item: UnitViewGallery) => (
    <div
      style={{ position: 'relative', display: 'inline-block', cursor: 'pointer' }}
      onClick={() => previewModal(item.image || '')}
    >
      <img
        className="rounded"
        alt="..."
        width={175}
        height={100}
        src={item.image || ''}
        style={{ objectFit: 'cover', display: 'block' }}
      />
      <span style={viewBadgeStyle}>{item.view}</span>
    </div>
  );

  const actionButtons = (item: UnitViewGallery) => (
    <Col className="col-auto">
      <Button
        className="btn-icon"
        color="secondary"
        size="sm"
        type="button"
        onClick={() => previewModal(item.image || '')}
      >
        <span className="btn-inner--icon">
          <i className="ni ni-image" />
        </span>
        <span className="btn-inner--text">Ver</span>
      </Button>
      {onDelete && (
        <Button
          className="btn-icon"
          color="danger"
          size="sm"
          onClick={() => onDelete(item)}
        >
          <span className="btn-inner--icon">
            <i className="ni ni-image" />
          </span>
          <span className="btn-inner--text">Eliminar</span>
        </Button>
      )}
    </Col>
  );

  let bodyList;

  if (onDrag === undefined) {
    bodyList = (
      <ListGroup className="list my--3" flush>
        {galleryForDisplay.map((item) => (
          <ListGroupItem key={item.view} className="px-0">
            <Row className="align-items-center">
              <Col className="col-auto bg-light border rounded p-3">
                {imageThumbnail(item)}
              </Col>
              <div className="col ml--2" />
              {actionButtons(item)}
            </Row>
          </ListGroupItem>
        ))}
      </ListGroup>
    );
  } else {
    bodyList = (
      <DragDropContext onDragEnd={onDragEnd}>
        <ListGroup className="list my--3" flush>
          <Droppable droppableId={description}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {provided.placeholder}
                {galleryForDisplay.map((item, idx) => (
                  <Draggable key={item.view} draggableId={item.view} index={idx}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.draggableProps}>
                        <ListGroupItem>
                          <Row className="align-items-center">
                            <Col className="col-auto">
                              <i
                                className="fa fa-grip-lines"
                                {...provided.dragHandleProps}
                              />
                            </Col>
                            <Col className="col-auto bg-light border rounded p-3">
                              {imageThumbnail(item)}
                            </Col>
                            <div className="col ml--2" />
                            {actionButtons(item)}
                          </Row>
                        </ListGroupItem>
                      </div>
                    )}
                  </Draggable>
                ))}
              </div>
            )}
          </Droppable>
        </ListGroup>
      </DragDropContext>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <h4>{description}</h4>
        </CardHeader>
        <CardBody>
          {galleryForDisplay.length === 0 && <p>No hay elementos que mostrar.</p>}
          {bodyList}
        </CardBody>
        {onDeleteAll && galleryForDisplay.length > 0 && (
          <CardFooter>
            <Button
              className="btn-icon"
              color="danger"
              size="sm"
              onClick={onDeleteAll}
            >
              <span className="btn-inner--icon">
                <i className="ni ni-image" />
              </span>
              <span className="btn-inner--text">Eliminar todas</span>
            </Button>
          </CardFooter>
        )}
      </Card>
      <Modal
        size="lg"
        className="modal-dialog-centered"
        isOpen={showModal}
        toggle={() => previewModal('')}
      >
        <div className="modal-header">
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => previewModal('')}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body">
          <UncontrolledCarousel
            controls={false}
            indicators={false}
            items={[
              {
                src: currentImage,
                altText: '',
                caption: '',
                header: '',
              },
            ]}
          />
        </div>
        <div className="modal-footer" />
      </Modal>
    </>
  );
};

export default AdminViewGallery;
